import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchLeadDetails,
  fetchLeadActivities,
  fetchRecentLeadOrders,
  fetchLeadConsultations,
  fetchLeadFinance,
  fetchLeadClaims,
  fetchPackagePolicies,
  fetchLeadClaimTransactions,
} from "../callWorkspaceApi";
import { getApiErrorMessage } from "utils/getApiErrorMessage";
import { getToken } from "../callWorkspace.utils";
import { sanitizeCards } from "../callLeadDetails/CallLeadDetails.utils";
import {
  emptyResource,
  emptyRecentResource,
  pageFromResponse,
  transactionPageFromResponse,
} from "../callLeadDetails/CallLeadDetailsContainer.utils";

const emptyFinance = () => ({
  cards: [],
  bank: null,
  cardsLoading: false,
  bankLoading: false,
  cardsLoaded: false,
  bankLoaded: false,
  cardsError: "",
  bankError: "",
});
const emptyPolicies = () => ({
  data: [],
  loading: false,
  loaded: false,
  error: "",
});

export const useLeadDrawerData = (open, selectedTask) => {
  const session = useRef(0);
  const [details, setDetails] = useState(null);
  const [activities, setActivities] = useState({ data: [], current_page: 1, last_page: 1, total: 0, loading: false, loaded: false, error: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [medicineOrders, setMedicineOrders] = useState(emptyRecentResource);
  const [labOrders, setLabOrders] = useState(emptyRecentResource);
  const [consultations, setConsultations] = useState(emptyResource);
  const [claims, setClaims] = useState(emptyResource);
  const [claimTransactions, setClaimTransactions] = useState(emptyResource);
  const [finance, setFinance] = useState(emptyFinance);
  const [policies, setPolicies] = useState(emptyPolicies);

  const reset = useCallback(() => {
    session.current += 1;
    setDetails(null);
    setActivities({ data: [], current_page: 1, last_page: 1, total: 0, loading: false, loaded: false, error: "" });
    setLoading(false);
    setError("");
    setMedicineOrders(emptyRecentResource());
    setLabOrders(emptyRecentResource());
    setConsultations(emptyResource());
    setClaims(emptyResource());
    setClaimTransactions(emptyResource());
    setFinance(emptyFinance());
    setPolicies(emptyPolicies());
  }, []);

  const loadActivities = useCallback((page = 1, id) => {
    if (!id) return Promise.resolve();
    const key = session.current;
    setActivities((state) => ({ ...state, loading: true, error: "" }));
    return fetchLeadActivities(getToken(), id, { page, page_size: 20 })
      .then((response) => {
        if (session.current !== key) return;
        const payload = response && response.data ? response.data : response || {};
        setActivities({ data: payload.data || [], current_page: payload.current_page || 1, last_page: payload.last_page || 1, total: payload.total || 0, loading: false, loaded: true, error: "" });
      })
      .catch((apiError) => {
        if (session.current === key) setActivities((state) => ({ ...state, loading: false, loaded: true, error: getApiErrorMessage(apiError) }));
      });
  }, []);

  useEffect(() => {
    if (!open || !selectedTask) {
      reset();
      return undefined;
    }
    const key = ++session.current;
    setDetails(null);
    setActivities({ data: [], current_page: 1, last_page: 1, total: 0, loading: false, loaded: false, error: "" });
    setLoading(true);
    setError("");
    fetchLeadDetails(getToken(), selectedTask.leadId, selectedTask.taskId)
      .then((response) => {
        if (session.current === key) {
          setDetails(response.data || response || null);
          loadActivities(1, selectedTask.leadId);
        }
      })
      .catch((apiError) => {
        if (session.current === key) setError(getApiErrorMessage(apiError));
      })
      .finally(() => {
        if (session.current === key) setLoading(false);
      });
    return () => {
      session.current += 1;
    };
  }, [open, selectedTask, reset, loadActivities]);

  const leadId = (details && details.lead && details.lead.id) || (selectedTask && selectedTask.leadId);
  const taskId = (details && details.task && details.task.id) || (selectedTask && selectedTask.taskId);
  // Do not expose the previous lead's response during the render immediately
  // after a row switch. The request effect clears it on the next commit.
  const currentDetails = details && String(details.task && details.task.id) === String(selectedTask && selectedTask.taskId)
    ? details
    : null;

  const refreshDetails = useCallback(() => {
    if (!open) return Promise.resolve();
    const key = session.current;
    setLoading(true);
    setError("");
    return fetchLeadDetails(getToken(), selectedTask.leadId, selectedTask.taskId)
      .then((response) => {
        if (session.current === key) {
          setDetails(response.data || response || null);
          loadActivities(1, selectedTask.leadId);
        }
      })
      .catch((apiError) => {
        if (session.current === key) {
          const message = getApiErrorMessage(apiError);
          setError(message);
        }
      })
      .finally(() => {
        if (session.current === key) setLoading(false);
      });
  }, [open, selectedTask, loadActivities]);

  const loadOrders = useCallback(
    (force) => {
      if (
        !details ||
        (!force && (medicineOrders.loaded || medicineOrders.loading))
      )
        return Promise.resolve();
      const key = session.current;
      setMedicineOrders((state) => ({ ...state, loading: true, error: "" }));
      setLabOrders((state) => ({ ...state, loading: true, error: "" }));
      return fetchRecentLeadOrders(getToken(), taskId)
        .then((response) => {
          if (session.current !== key) return;
          const payload = response.data || response || {};
          const period = payload.period || null;
          setMedicineOrders({
            data: payload.medicine_orders || [],
            period,
            loading: false,
            loaded: true,
            error: "",
          });
          setLabOrders({
            data: payload.lab_orders || [],
            period,
            loading: false,
            loaded: true,
            error: "",
          });
        })
        .catch((apiError) => {
          if (session.current !== key) return;
          const message = getApiErrorMessage(apiError);
          setMedicineOrders((state) => ({
            ...state,
            loading: false,
            loaded: true,
            error: message,
          }));
          setLabOrders((state) => ({
            ...state,
            loading: false,
            loaded: true,
            error: message,
          }));
        });
    },
    [details, medicineOrders.loaded, medicineOrders.loading, taskId],
  );

  const loadPage = useCallback(
    (kind, page, force) => {
      if (!leadId) return Promise.resolve();
      const config =
        kind === "consultations"
          ? [consultations, setConsultations, fetchLeadConsultations, {}]
          : kind === "claims"
            ? [claims, setClaims, fetchLeadClaims, { page_size: 10 }]
            : [
                claimTransactions,
                setClaimTransactions,
                fetchLeadClaimTransactions,
                { page_size: 10 },
              ];
      if (!force && (config[0].loaded || config[0].loading))
        return Promise.resolve();
      const key = session.current;
      config[1]((state) => ({ ...state, loading: true, error: "" }));
      return config[2](getToken(), leadId, { ...config[3], page: page || 1 })
        .then((response) => {
          if (session.current !== key) return;
          const data =
            kind === "transactions"
              ? transactionPageFromResponse(response)
              : pageFromResponse(response);
          config[1]({ data, loading: false, loaded: true, error: "" });
        })
        .catch((apiError) => {
          if (session.current === key)
            config[1]((state) => ({
              ...state,
              loading: false,
              loaded: true,
              error: getApiErrorMessage(apiError),
            }));
        });
    },
    [claimTransactions, claims, consultations, leadId],
  );

  const load = useCallback(
    (kind, force = false) => {
      if (kind === "orders") return loadOrders(force);
      if (
        kind === "consultations" ||
        kind === "claims" ||
        kind === "transactions"
      )
        return loadPage(kind, 1, force);
      if (kind === "finance") {
        if (
          !leadId ||
          (!force && (finance.cardsLoaded || finance.cardsLoading))
        )
          return Promise.resolve();
        const key = session.current;
        setFinance((state) => ({
          ...state,
          cardsLoading: true,
          bankLoading: true,
          cardsError: "",
          bankError: "",
        }));
        return fetchLeadFinance(getToken(), leadId)
          .then((response) => {
            if (session.current === key) {
              const payload = response.data || {};
              setFinance({
                cards: sanitizeCards(payload.cards),
                bank: payload.lead_detail || null,
                cardsLoading: false,
                bankLoading: false,
                cardsLoaded: true,
                bankLoaded: true,
                cardsError: "",
                bankError: "",
              });
            }
          })
          .catch((apiError) => {
            if (session.current === key)
              setFinance((state) => ({
                ...state,
                cardsLoading: false,
                bankLoading: false,
                cardsLoaded: true,
                bankLoaded: true,
                cardsError: getApiErrorMessage(apiError),
                bankError: getApiErrorMessage(apiError),
              }));
          });
      }
      return Promise.resolve();
    },
    [finance.cardsLoaded, finance.cardsLoading, leadId, loadOrders, loadPage],
  );

  const loadPolicies = useCallback(
    (force) => {
      if (!leadId || (!force && (policies.loaded || policies.loading)))
        return Promise.resolve();
      const packageRows =
        (details.lead || {}).lead_catalogue_package || [];
      const packageIds = Array.from(
        new Set(packageRows.map((item) => item.package_id).filter(Boolean)),
      );
      if (!packageIds.length) {
        setPolicies({ data: [], loading: false, loaded: true, error: "" });
        return Promise.resolve();
      }
      const key = session.current;
      setPolicies((state) => ({ ...state, loading: true, error: "" }));
      return Promise.all(
        packageIds.map((packageId) =>
          fetchPackagePolicies(getToken(), packageId).then((response) => ({
            packageId,
            data: Array.isArray(response.data) ? response.data : [],
          })),
        ),
      )
        .then((data) => {
          if (session.current === key)
            setPolicies({ data, loading: false, loaded: true, error: "" });
        })
        .catch((apiError) => {
          if (session.current === key)
            setPolicies({
              data: [],
              loading: false,
              loaded: true,
              error: getApiErrorMessage(apiError),
            });
        });
    },
    [details, leadId, policies.loaded, policies.loading],
  );

  return {
    details: currentDetails,
    refreshDetails,
    activities,
    loadActivities,
    loading,
    error,
    medicineOrders,
    labOrders,
    consultations,
    finance,
    claims,
    claimTransactions,
    policies,
    load,
    loadPolicies,
    loadPage,
  };
};
